import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/session/store';
import { ChoiceBlock } from './ChoiceBlock';
import { MediaShelf } from './media/MediaShelf';
import { MessageBubble } from './MessageBubble';
import { SessionIntro } from './SessionIntro';
import { ThinkingBubble } from './ThinkingBubble';

/** How close to the bottom still counts as "following along", in px. */
const FOLLOW_THRESHOLD = 140;

export function Transcript() {
  const messages = useSession((state) => state.messages);
  const thinking = useSession((state) => state.thinking);
  const introDismissed = useSession((state) => state.introDismissed);

  const scroller = useRef<HTMLDivElement>(null);
  const end = useRef<HTMLDivElement>(null);
  // Only auto-scrolled while the user is at the bottom: a reply streams for seconds, and yanking
  // the view back would make reading anything above it impossible.
  const following = useRef(true);

  useEffect(() => {
    if (following.current) end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking]);

  const showIntro = !introDismissed && messages.length === 0 && !thinking;

  return (
    <main
      ref={scroller}
      onScroll={() => {
        const node = scroller.current;
        if (!node) return;
        following.current =
          node.scrollHeight - node.scrollTop - node.clientHeight < FOLLOW_THRESHOLD;
      }}
      className="scroll-slim flex-1 overflow-y-auto overflow-x-hidden"
    >
      <div
        className={cn(
          'mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 py-8 sm:px-6',
          // While the prompt is up there is nothing else in the transcript, so it is anchored to the
          // bottom of the scroller — it reads as an introduction to the composer under it rather than
          // as a headline floating in an empty pane.
          showIntro && 'min-h-full justify-end',
        )}
      >
        {showIntro && <SessionIntro />}

        {messages.map((message) => (
          <motion.div
            key={message.id}
            layout="position"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            {message.kind === 'text' && <MessageBubble message={message} />}
            {message.kind === 'results' && <MediaShelf results={message.results} />}
            {message.kind === 'choice' && <ChoiceBlock choice={message.choice} />}
          </motion.div>
        ))}

        <AnimatePresence>{thinking && <ThinkingBubble />}</AnimatePresence>

        <div ref={end} className="h-px" />
      </div>
    </main>
  );
}
